import { Badge } from "@component/badge";
import { Button } from "@component/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@component/dialog";
import { Label } from "@component/label";
import { Textarea } from "@component/textarea";
import { Tooltip } from "@component/tooltip";
import { useSnackbar } from "@hook/use-snackbar";
import { sendSubscriptionInquiry } from "@service/subscription";
import { catchErrorLog } from "@util/sentry";
import { InfoIcon } from "lucide-react";
import { useState } from "react";

interface DialogViewPlanProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedPlan: "12month" | "6month" | null;
    setSelectedPlan: (plan: "12month" | "6month" | null) => void;
    requestInfoNote: string;
    setRequestInfoNote: (note: string) => void;
    getPlanMonthlyPrice: (plan: "12month" | "6month") => string;
    getPlanTotalPrice: (plan: "12month" | "6month") => string;
    getPayAsYouGoPrice: (type: "cvScreening" | "aiAssessment") => string;
}

export default function DialogViewPlan({
    open,
    onOpenChange,
    selectedPlan,
    setSelectedPlan,
    requestInfoNote,
    setRequestInfoNote,
    getPlanMonthlyPrice,
    getPlanTotalPrice,
    getPayAsYouGoPrice,
}: DialogViewPlanProps) {
    const snackbar = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmitInquiry = () => {
        setIsLoading(true);
        sendSubscriptionInquiry(selectedPlan || "", requestInfoNote)
            .then((resp) => {
                if (resp.status === 200) {
                    onOpenChange(false);
                    setRequestInfoNote("");
                    setSelectedPlan(null);
                    snackbar.showSuccess("Your inquiry has been submitted successfully.");
                }
            })
            .catch((error) => {
                onOpenChange(false);
                snackbar.showError("Failed to submit inquiry. Please try again later.");
                catchErrorLog(`failed to send subscription inquiry: ${error}`);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl" closeButtonClassName="top-6 right-6">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <DialogTitle>
                                Get in Touch
                            </DialogTitle>
                            <DialogDescription className="mt-2">
                                Tell us a bit about your hiring needs, and we’ll recommend the right pricing for you.
                            </DialogDescription>
                        </div>
                        {/* Demo Currency Selector */}
                        {/* <div className="flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground">Currency (Demo):</Label>
                            <Select
                                value={selectedCurrency}
                                onValueChange={(value) => setSelectedCurrency(value as Currency)}
                            >
                                <SelectTrigger className="w-[140px] h-8">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="SGD">SGD</SelectItem>
                                    <SelectItem value="MYR">MYR</SelectItem>
                                    <SelectItem value="IDR">IDR</SelectItem>
                                </SelectContent>
                            </Select>
                        </div> */}
                    </div>
                </DialogHeader>
                <div className="space-y-0 py-0">
                    {/* Subscription Plans */}
                    <div className="space-y-3 hidden">
                        <Label className="text-sm font-medium">Select a Plan</Label>
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Annual Plan */}
                                <div
                                    onClick={() => setSelectedPlan("12month")}
                                    className={`relative cursor-pointer rounded-xl border-2 p-8 transition-all shadow-sm hover:shadow-md ${selectedPlan === "12month"
                                        ? "border-blue-600 bg-gradient-to-br from-blue-50 to-blue-50/30 dark:from-blue-950/30 dark:to-blue-950/10 shadow-blue-100 dark:shadow-blue-900/20"
                                        : "border-border hover:border-blue-400 bg-white dark:bg-card"
                                        }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="flex items-center h-6">
                                            <input
                                                type="checkbox"
                                                checked={selectedPlan === "12month"}
                                                onChange={() => setSelectedPlan("12month")}
                                                className="h-5 w-5 rounded border-2 border-border bg-white dark:bg-background checked:bg-blue-600 checked:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 cursor-pointer transition-all accent-blue-600"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0 space-y-5">
                                            {/* Header with Badge */}
                                            <div className="flex items-center justify-between gap-3">
                                                <h4 className="text-lg font-bold text-foreground leading-6">
                                                    Annual Plan
                                                </h4>
                                                <Badge
                                                    variant="default"
                                                    className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-600 hover:to-blue-500 text-white px-3 py-1 text-xs font-semibold rounded-full shadow-sm pointer-events-none"
                                                >
                                                    Save 10%
                                                </Badge>
                                            </div>

                                            {/* Pricing */}
                                            <div className="space-y-2">
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-3xl font-bold text-foreground">
                                                        {getPlanMonthlyPrice("12month")}
                                                    </span>
                                                    <span className="text-base text-muted-foreground font-medium">
                                                        / month
                                                    </span>
                                                </div>
                                                <p className="text-sm text-muted-foreground">
                                                    {getPlanTotalPrice("12month")} billed annually
                                                </p>
                                            </div>

                                            {/* Features */}
                                            <div className="space-y-3">
                                                <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                                    Includes every month
                                                </h5>
                                                <ul className="space-y-2.5 text-sm">
                                                    <li className="flex items-start gap-2.5">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                                                        <span className="text-foreground">500 CV screenings</span>
                                                    </li>
                                                    <li className="flex items-start gap-2.5">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-foreground">100 completed AI screenings</span>
                                                            <Tooltip content="Only completed interviews count towards your monthly quota">
                                                                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                                            </Tooltip>
                                                        </div>
                                                    </li>
                                                    <li className="flex items-start gap-2.5">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                                                        <span className="text-foreground">Unlimited users</span>
                                                    </li>
                                                    <li className="flex items-start gap-2.5">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                                                        <span className="text-foreground">Full ATS and candidate workflow</span>
                                                    </li>
                                                </ul>
                                            </div>

                                            {/* Usage Billing - Muted with Bullet Points */}
                                            <div className="pt-2">
                                                <p className="text-xs text-muted-foreground mb-2">
                                                    Usage beyond your quota is billed separately:
                                                </p>
                                                <ul className="space-y-1 text-xs text-muted-foreground">
                                                    <li className="flex items-start gap-2">
                                                        <div className="h-1 w-1 rounded-full bg-muted-foreground mt-1.5 flex-shrink-0" />
                                                        <span>{getPayAsYouGoPrice("cvScreening")} per CV screening</span>
                                                    </li>
                                                    <li className="flex items-start gap-2">
                                                        <div className="h-1 w-1 rounded-full bg-muted-foreground mt-1.5 flex-shrink-0" />
                                                        <span>{getPayAsYouGoPrice("aiAssessment")} per AI screening completed</span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Quarterly Plan */}
                                <div
                                    onClick={() => setSelectedPlan("6month")}
                                    className={`relative cursor-pointer rounded-xl border-2 p-8 transition-all shadow-sm hover:shadow-md ${selectedPlan === "6month"
                                        ? "border-blue-600 bg-gradient-to-br from-blue-50 to-blue-50/30 dark:from-blue-950/30 dark:to-blue-950/10 shadow-blue-100 dark:shadow-blue-900/20"
                                        : "border-border hover:border-blue-400 bg-white dark:bg-card"
                                        }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="flex items-center h-6">
                                            <input
                                                type="checkbox"
                                                checked={selectedPlan === "6month"}
                                                onChange={() => setSelectedPlan("6month")}
                                                className="h-5 w-5 rounded border-2 border-border bg-white dark:bg-background checked:bg-blue-600 checked:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 cursor-pointer transition-all accent-blue-600"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0 space-y-5">
                                            {/* Header */}
                                            <div className="flex items-center justify-between gap-3">
                                                <h4 className="text-lg font-bold text-foreground leading-6">
                                                    Quarterly Plan
                                                </h4>
                                            </div>

                                            {/* Pricing */}
                                            <div className="space-y-2">
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-3xl font-bold text-foreground">
                                                        {getPlanMonthlyPrice("6month")}
                                                    </span>
                                                    <span className="text-base text-muted-foreground font-medium">
                                                        / month
                                                    </span>
                                                </div>
                                                <p className="text-sm text-muted-foreground">
                                                    {getPlanTotalPrice("6month")} billed quarterly
                                                </p>
                                            </div>

                                            {/* Features */}
                                            <div className="space-y-3">
                                                <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                                    Includes every month
                                                </h5>
                                                <ul className="space-y-2.5 text-sm">
                                                    <li className="flex items-start gap-2.5">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                                                        <span className="text-foreground">500 CV screenings</span>
                                                    </li>
                                                    <li className="flex items-start gap-2.5">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-foreground">100 completed AI screenings</span>
                                                            <Tooltip content="Only completed interviews count towards your monthly quota">
                                                                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                                            </Tooltip>
                                                        </div>
                                                    </li>
                                                    <li className="flex items-start gap-2.5">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                                                        <span className="text-foreground">Unlimited users</span>
                                                    </li>
                                                    <li className="flex items-start gap-2.5">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                                                        <span className="text-foreground">Full ATS and candidate workflow</span>
                                                    </li>
                                                </ul>
                                            </div>

                                            {/* Usage Billing - Muted with Bullet Points */}
                                            <div className="pt-2">
                                                <p className="text-xs text-muted-foreground mb-2">
                                                    Usage beyond your quota is billed separately:
                                                </p>
                                                <ul className="space-y-1 text-xs text-muted-foreground">
                                                    <li className="flex items-start gap-2">
                                                        <div className="h-1 w-1 rounded-full bg-muted-foreground mt-1.5 flex-shrink-0" />
                                                        <span>{getPayAsYouGoPrice("cvScreening")} per CV screening</span>
                                                    </li>
                                                    <li className="flex items-start gap-2">
                                                        <div className="h-1 w-1 rounded-full bg-muted-foreground mt-1.5 flex-shrink-0" />
                                                        <span>{getPayAsYouGoPrice("aiAssessment")} per AI screening completed</span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Additional Notes */}
                    <div className="space-y-2">
                        <Label htmlFor="request-note" className="text-sm font-medium">
                            Notes / Questions
                        </Label>
                        <Textarea
                            id="request-note"
                            placeholder="Let us know what you need, e.g. team size, hiring volume, specific features..."
                            value={requestInfoNote}
                            onChange={(e) => setRequestInfoNote(e.target.value)}
                            className="min-h-[100px] resize-none"
                        />
                    </div>

                    <div>
                        <p className="text-xs text-muted-foreground pt-1">
                            Our team will receive your request and respond to your
                            registered email address within 24 hours.
                        </p>
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        disabled={isLoading}
                        onClick={() => {
                            onOpenChange(false);
                            setRequestInfoNote("");
                            setSelectedPlan(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitInquiry}
                        disabled={!selectedPlan || isLoading || requestInfoNote === ""}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {isLoading ? "Submitting..." : "Submit Inquiry"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
